import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getBrandDiscountById,
  putBrandDiscount,
} from "../../app/services/brandDiscountService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";

import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";
import {
  BrandDiscount,
  defaultBrandDiscount,
} from "../../models/responses/brandDiscount";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { getCarBrands } from "../../app/services/carBrandService";
import { calculateFromPercentage } from "../../app/services/calculationsHelperService";

interface Props {}

const BrandDiscountEdit: FC<Props> = () => {
  const { brandDiscountId } = useParams<{ brandDiscountId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const isWaitingCustomerPutResponse = useSelector(
    (state: RootState) => state.customer.isWaitingCustomerPutResponse
  );
  const carBrands = useSelector(
    (state: RootState) => state.carBrand.carBrandList || []
  );
  const brandDiscount = useSelector(
    (state: RootState) => state.brandDiscount.brandDiscount
  );
  const [brandDiscountData, setBrandDiscountData] =
    useState<BrandDiscount>(defaultBrandDiscount);

  useEffect(() => {
    dispatch(getBrandDiscountById(instance, account, brandDiscountId));
  }, [account, accounts, dispatch, instance, brandDiscountId]);
  useEffect(() => {
    if (brandDiscount) {
      setBrandDiscountData({ ...brandDiscount, carBrandId: brandDiscount.carBrand?.id || "" });
    }
  }, [brandDiscount]);

  useEffect(() => {
    dispatch(getCarBrands(instance, account));
  }, [account, accounts, dispatch, instance]);

  const workshopSalesDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (isNaN(parseFloat(value))) value = "0";
    setBrandDiscountData({
      ...brandDiscountData,
      workshopSalesDiscount: value,
    });
  };

  const storeDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBrandDiscountData({
      ...brandDiscountData,
      storeDiscount: e.target.value,
    });
  };

  const handleUpdateBrandDiscount = () => {
    setDisplayError(false);
    dispatch(putBrandDiscount(instance, account, brandDiscountData, onSuccess));
  };

  const onSuccess = (brandDiscountId: string | null) => {
    if (brandDiscountId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../../${brandDiscountId}`, { relative: "path" });
    }, 500);
  };

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item href="/brand-discounts">Rabatter</Breadcrumb.Item>
        <Breadcrumb.Item active>Uppdatera rabatt</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form className="w-3">
        <Row>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Bilmärke *</Form.Label>
              <Form.Select
                aria-label="Bilmärke"
                value={brandDiscountData.carBrand?.id || ""}
                onChange={(e) =>
                  setBrandDiscountData({
                    ...brandDiscountData,
                    carBrandId: e.target.value,
                  })
                }
              >
                <option value="">Välj bilmärke</option>{" "}
                {/* Ny default option */}
                {carBrands.map((carBrand) => (
                  <option key={carBrand.id} value={carBrand.id}>
                    {carBrand.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>VSN *</Form.Label>
              <Form.Control
                type="text"
                value={brandDiscountData.workshopSalesDiscount}
                onInput={workshopSalesDiscountInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>SPN *</Form.Label>
              <Form.Control
                type="text"
                value={brandDiscountData.storeDiscount}
                onInput={storeDiscountInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleUpdateBrandDiscount}
            disabled={
              !(
                brandDiscountData.carBrand?.id !== undefined &&
                brandDiscountData.carBrand?.id.length > 0 &&
                !isWaitingCustomerPutResponse
              )
            }
          >
            Uppdatera rabatt
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
    </div>
  );
};

export default BrandDiscountEdit;
